/**
 * Document Viewer Panel Component
 * Main container for document viewing with tabs, resizing, edit mode, plan tabs, and page panels
 */

import { useState, useEffect, useRef } from 'react';
import { logger } from '../../utils/logger';
import DocumentViewerTabs from './DocumentViewerTabs';
import DocumentViewerContent from './DocumentViewerContent';
import PdfViewer from './PdfViewer';
import PagePanel from '../panels/PagePanel';
import { SVGImageViewer } from '../diagram/SVGImageViewer';
import { FlowDiagramViewer } from '../diagram/FlowDiagramViewer';
import { RequirementsIndex } from '../coding/RequirementsIndex';
import { DiagramState } from '../../types/diagram-types';
import { diagramExportApi } from '../../services/api';
import { 
  exportBoeingWDMSvg, 
  isDocumentsFunctionConfigured,
  generateBoeingWDMSvg, 
  isRoutingEngineConfigured 
} from '../../services/routing-engine';
import { authService } from '../../services/auth-service';
import type { AeroDocument } from '../../types/document-schema';
import type { DocumentOperation } from '../../types/document-operations';
import type { PlanDocument } from '../../types/plan-document';
import type { TabType, PagePanelId, MermaidDiagramType } from '../../types/panel-types';
import type { ActiveProject } from '../../contexts/AIContext';
import type { LockColorCode } from '../../types';
import { Spinner, Button } from '@/components/ui';

interface OpenedDocument {
  id: string;
  tabId: number;
  title: string;
  tabType?: TabType; // 'document' | 'plan' | 'page' | 'diagram' | 'mermaid'
  // Document-specific fields
  documentNumber?: string;
  projectId?: string;
  type?: 'project' | 'company' | 'standard' | 'prior-project' | 'plan';
  canEdit?: boolean;
  jsonDocument?: AeroDocument;
  version?: string;
  // Plan-specific fields
  planDocument?: PlanDocument;
  // Page panel-specific fields
  panelType?: PagePanelId;
  searchQuery?: string;
  // Diagram-specific fields (wiring diagrams)
  diagramId?: string;
  // Mermaid/Flow diagram-specific fields
  mermaidDiagramId?: string;
  mermaidType?: MermaidDiagramType;
  svgUrl?: string;
  // PDF viewer - for generated PDFs opened directly
  pdfUrl?: string;
  // Coding project-specific fields
  codingProjectId?: string;
  projectType?: string;
  status?: string;
}

interface DocumentViewerPanelProps {
  documents: OpenedDocument[];
  activeTabId: number | null;
  editingTabId: number | null;
  isVisible: boolean;
  width: number;
  onSelectTab: (tabId: number) => void;
  onCloseTab: (tabId: number) => void;
  onCloseAll: () => void;
  onBack: (tabId: number) => void;  // Navigate back without closing tab
  onEnableEdit: (tabId: number, jsonDocument: AeroDocument, version: string) => void;
  onDisableEdit: (documentNumber?: string) => void;
  onUpdate?: (operations: DocumentOperation[]) => void;
  onSave?: () => void;
  onDocumentOperation?: (operation: DocumentOperation) => void;
  onDocumentLoaded?: (tabId: number, jsonDocument: AeroDocument, version: string, s3VersionId?: string, s3JsonKey?: string) => void;
  onExecutePlan?: (plan: PlanDocument) => Promise<void>;
  onReorderTabs?: (fromIndex: number, toIndex: number) => void;
  // Page panel callbacks
  onOpenDocument?: (doc: { 
    id: string; 
    documentNumber?: string;
    projectId?: string;
    title: string;
    type: 'project' | 'company' | 'standard' | 'prior-project';
    canEdit?: boolean;
  }) => void;
  onOpenDiagram?: (diagram: {
    id: string;
    diagramId: string;
    projectId: string;
    title: string;
  }) => void;
  onOpenFlowDiagram?: (diagram: {
    id: string;
    diagramId: string;
    documentNumber?: string; // New: unified API uses documentNumber
    projectId: string;
    title: string;
    mermaidType: MermaidDiagramType;
    svgUrl?: string;
  }) => void;
  onOpenCodingProject?: (codingProject: {
    id: string;
    codingProjectId: string;
    projectId: string;
    title: string;
    projectType: string;
    status: string;
  }) => void;
  // Diagram state (passed from AIContext when in diagram mode)
  diagramState?: DiagramState | null;
  // Wiring Diagram AI edit mode (matching document pattern)
  activeDiagramEditId?: string | null;
  isDiagramLinking?: boolean;
  onEnableDiagramAIEdit?: (diagramId: string, title: string, projectId: string) => void;
  onDisableDiagramAIEdit?: () => void;
  // Flowchart (Mermaid) AI edit mode
  activeFlowchartEditId?: string | null;
  onEnableFlowchartAIEdit?: (mermaidDiagramId: string, title: string, projectId: string) => void;
  onDisableFlowchartAIEdit?: () => void;
  // Coding Project AI edit mode
  activeCodingProjectEditId?: string | null;
  onEnableCodingProjectAIEdit?: (codingProjectId: string, title: string, projectId: string) => void;
  onDisableCodingProjectAIEdit?: () => void;
  // Mermaid diagram update versions (to trigger refetch when AI updates)
  mermaidVersions?: Record<string, number>;
  // Coding project update versions (to trigger refetch when AI updates)
  codingProjectVersions?: Record<string, number>;
  // Project Workspace props
  activeProject?: ActiveProject | null;
  onSwitchProject?: (projectId: string | null) => void;
  onOpenProjectSwitcher?: () => void;
  // Document lock colors for visual pairing
  documentLockColors?: Record<string, LockColorCode>;
  // User avatar props
  userEmail?: string;
  onOpenSettings?: (panelId: PagePanelId) => void;
  onLogout?: () => void;
  // Plan execution: send message to AI (with optional mode override for async state handling)
  onSendMessage?: (message: string, options?: { modeOverride?: 'ask' | 'agent' | 'plan' | 'diagram' | 'develop' }) => void;
  // Plan execution: open document by number (simpler than onOpenDocument)
  onOpenDocumentByNumber?: (doc: { documentNumber: string; projectId: string }) => void;
  // Plan execution: set AI mode
  onSetAiMode?: (mode: 'ask' | 'agent' | 'plan' | 'diagram' | 'develop') => void;
  // PDF viewer callback
  onOpenPdfInViewer?: (pdfUrl: string, title: string) => void;
}

// ============================================================================
// Diagram Canvas Wrapper (Boeing WDM only)
// ============================================================================

interface DiagramCanvasWrapperProps {
  title: string;
  diagramId: string;
  projectId: string;
  diagramState: DiagramState | null | undefined;
  width: number;
  // AI edit mode props (matching document pattern)
  isAIEditing?: boolean;
  isLinking?: boolean;
  onEnableAIEdit?: () => void;
  onDisableAIEdit?: () => void;
}

function DiagramCanvasWrapper({ 
  title, 
  diagramId, 
  projectId, 
  diagramState, 
  width: _width,
  isAIEditing = false,
  isLinking = false,
  onEnableAIEdit,
  onDisableAIEdit,
}: DiagramCanvasWrapperProps) {
  // Always use Boeing WDM mode (C engine SVG rendering with proper schematic symbols)
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  
  // State for Boeing WDM SVG fetching
  const [boeingSvgUrl, setBoeingSvgUrl] = useState<string | null>(null);
  const [boeingSvgLoading, setBoeingSvgLoading] = useState(false);
  const [boeingSvgError, setBoeingSvgError] = useState<string | null>(null);
  
  // Track the last rendered version to avoid re-rendering unnecessarily
  const [lastRenderedVersion, setLastRenderedVersion] = useState<number | undefined>(undefined);
  
  // Fetch Boeing WDM SVG when diagram changes
  // Only regenerate when version changes (AI updates), not on every page load
  useEffect(() => {
    if (!projectId || !diagramId) {
      return;
    }
    
    // Handle cleared/empty diagram - clear the SVG display
    if (!diagramState || !diagramState.devices || diagramState.devices.length === 0) {
      logger.debug('[BoeingWDM] Diagram is empty, clearing SVG display');
      setBoeingSvgUrl(null);
      setBoeingSvgLoading(false);
      setBoeingSvgError(null);
      setLastRenderedVersion(undefined);
      return;
    }
    
    // Skip if we already have an SVG for this version/device count
    const currentVersion = diagramState?.version;
    const deviceCount = diagramState?.devices?.length || 0;
    if (boeingSvgUrl && lastRenderedVersion === currentVersion && deviceCount > 0) {
      logger.debug('[BoeingWDM] Using cached SVG, version:', currentVersion);
      return;
    }
    
    // Track if this effect is still mounted (prevents race conditions)
    let isCancelled = false;
    
    logger.debug('[BoeingWDM] Fetching SVG, version:', currentVersion, 'devices:', deviceCount);
    
    const fetchBoeingSvg = async () => {
      setBoeingSvgLoading(true);
      setBoeingSvgError(null);
      
      try {
        const docsFunctionConfigured = isDocumentsFunctionConfigured();
        const cEngineFunctionConfigured = isRoutingEngineConfigured();
        
        logger.debug('[BoeingWDM] ===== ROUTING ENGINE CHECK =====');
        logger.debug('[BoeingWDM] Documents Function URL (with S3 cache):', docsFunctionConfigured);
        logger.debug('[BoeingWDM] C Engine Function URL (no cache):', cEngineFunctionConfigured);
        logger.debug('[BoeingWDM] diagramState:', !!diagramState, 'devices:', diagramState?.devices?.length);
        logger.debug('[BoeingWDM] ================================');
        
        // PRIMARY PATH: Documents Function URL (with S3 caching)
        // Returns presigned S3 URL - fast on cache hit, generates on cache miss
        if (docsFunctionConfigured && projectId && diagramId) {
          logger.debug('[BoeingWDM] Using Documents Function URL (with S3 cache)');
          
          const tokens = await authService.getTokens();
          if (!tokens?.accessToken) {
            throw new Error('Authentication token not available');
          }
          
          // Use accessToken (not idToken) - backend verifier expects access tokens
          const result = await exportBoeingWDMSvg(projectId, diagramId, tokens.accessToken);
          
          if (isCancelled) return;
          
          logger.debug('[BoeingWDM] SVG export complete:', {
            cached: result.cached,
            url: result.url.substring(0, 50) + '...',
          });
          setBoeingSvgUrl(result.url);
          setLastRenderedVersion(currentVersion);
          return;
        }
        
        // FALLBACK PATH 1: Direct C Engine Function URL (no S3 caching)
        if (cEngineFunctionConfigured && diagramState) {
          logger.debug('[BoeingWDM] Using C Engine Function URL (no cache)');
          
          // Convert diagram state to routing engine format
          interface DiagramDevice {
            id: string;
            type?: string;
            position: string | { x: number; y: number };
            pins: string | Array<{ pin?: string; pin_number?: string; id?: string; location?: { x?: number; y?: number } }>;
          }
          interface DiagramWire {
            id?: string;
            wire_id?: string;
            from?: { device?: string; pin?: string };
            to?: { device?: string; pin?: string };
            fromDevice?: string;
            fromPin?: string;
            toDevice?: string;
            toPin?: string;
          }
          const routingDevices = (diagramState.devices || []).map((d: DiagramDevice) => {
            const pos = typeof d.position === 'string' ? JSON.parse(d.position) : (d.position || { x: 0, y: 0 });
            const pins = typeof d.pins === 'string' ? JSON.parse(d.pins) : (d.pins || []);
            
            const typeMap: Record<string, number> = {
              'connector': 0, 'terminal_block': 1, 'junction': 2,
              'module': 3, 'switch': 4, 'ground_bus': 5, 'splice': 6,
            };
            
            return {
              id: d.id,
              type: typeMap[d.type || 'connector'] || 0,
              x: pos.x,
              y: pos.y,
              pins: pins.map((p: { pin?: string; pin_number?: string; id?: string; location?: { x?: number; y?: number } }, idx: number) => ({
                id: p.pin || p.pin_number || p.id || String(idx + 1),
                x: p.location?.x ?? 16,
                y: p.location?.y ?? 0,
              })),
            };
          });
          
          const routingConnections = (diagramState.wires || []).map((w: DiagramWire) => ({
            wire_id: w.id || w.wire_id,
            from_device: w.from?.device || w.fromDevice,
            from_pin: w.from?.pin || w.fromPin || '1',
            to_device: w.to?.device || w.toDevice,
            to_pin: w.to?.pin || w.toPin || '1',
            priority: 1,
          }));
          
          // Calculate canvas size with proper margins for N+1 channel routing
          // Minimum 1000x700 for proper channel spacing
          let maxX = 1000, maxY = 700;
          routingDevices.forEach((d) => {
            // Add 150mm margin after rightmost/bottommost device for routing channels
            maxX = Math.max(maxX, d.x + 150);
            maxY = Math.max(maxY, d.y + 150);
          });
          
          const result = await generateBoeingWDMSvg({
            action: 'svg',
            width_mm: Math.ceil(maxX),
            height_mm: Math.ceil(maxY),
            devices: routingDevices,
            connections: routingConnections,
          });
          
          if (isCancelled) return;
          
          if (result.svg) {
            const svgBlob = new Blob([result.svg], { type: 'image/svg+xml' });
            const svgDataUrl = URL.createObjectURL(svgBlob);
            logger.debug('[BoeingWDM] SVG generated via C Engine (uncached):', result.metrics);
            setBoeingSvgUrl(svgDataUrl);
            setLastRenderedVersion(currentVersion);
            return;
          }
          throw new Error('No SVG returned from routing engine');
        }
        
        // FALLBACK PATH 2: API Gateway (may timeout for complex diagrams)
        logger.debug('[BoeingWDM] No Function URLs configured, using API Gateway');
        const result = await diagramExportApi.export(projectId, diagramId, 'svg');
        
        if (isCancelled) return;
        
        if (result.url) {
          logger.debug('[BoeingWDM] SVG URL from API Gateway:', result.url.substring(0, 60) + '...');
          setBoeingSvgUrl(result.url);
          setLastRenderedVersion(currentVersion);
        } else {
          throw new Error('No SVG URL returned from export API');
        }
      } catch (err: unknown) {
        // Ignore cancelled/aborted requests (not real errors)
        if (isCancelled || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          logger.debug('[BoeingWDM] Request cancelled, ignoring error');
          return;
        }
        
        logger.error('[BoeingWDM] SVG generation failed:', err.message);
        setBoeingSvgError(err.message || 'Failed to load Boeing WDM diagram');
      } finally {
        if (!isCancelled) {
          setBoeingSvgLoading(false);
        }
      }
    };
    
    fetchBoeingSvg();
    
    // Cleanup function - mark as cancelled if deps change before fetch completes
    return () => {
      isCancelled = true;
    };
  // Dependencies: re-run when IDs change, diagram state becomes available, or version changes
  }, [projectId, diagramId, diagramState, diagramState?.version, diagramState?.devices?.length, boeingSvgUrl, lastRenderedVersion]);

  const handleExport = async (format: 'dxf' | 'svg') => {
    if (!projectId || !diagramId) {
      setExportError('Missing project or diagram ID');
      return;
    }

    setExporting(true);
    setExportError(null);

    try {
      const result = await diagramExportApi.export(projectId, diagramId, format);
      
      if (result.downloadUrl) {
        // Trigger download
        const link = document.createElement('a');
        link.href = result.downloadUrl;
        link.download = result.fileName || `diagram.${format}`;
        link.click();
      } else if (result.content) {
        // Create blob download
        const blob = new Blob([result.content], { 
          type: format === 'dxf' ? 'application/dxf' : 'image/svg+xml' 
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${diagramId}.${format}`;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (err: unknown) {
      setExportError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header - matches EditorToolbar styling */}
      <div className="flex-shrink-0 flex items-center gap-3 px-3 py-2 bg-muted border-b border-border">
        <h2 className="text-sm font-medium text-foreground truncate flex-1">
          {title}
        </h2>
        
        {/* AI Editing toggle - matches document pattern */}
        {isAIEditing ? (
          <Button
            onClick={onDisableAIEdit}
            variant="secondary"
            size="sm"
            title="Stop AI editing and return to view-only mode"
            aria-label="Stop AI editing and return to view-only mode"
          >
            <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
            Stop Editing
          </Button>
        ) : (
          <Button
            onClick={onEnableAIEdit}
            disabled={isLinking}
            variant="info"
            size="sm"
            title="Enable AI editing to place connectors, route wires, and modify the diagram"
            aria-label="Enable AI editing to place connectors, route wires, and modify the diagram"
          >
            {isLinking ? (
              <svg className="w-4 h-4 mr-1.5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <circle cx="12" cy="12" r="10" opacity="0.25" />
                <path d="M12 2a10 10 0 0 1 10 10" />
              </svg>
            ) : (
              <svg className="w-4 h-4 mr-1.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 20h9" />
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
            )}
            {isLinking ? 'Linking...' : 'Enable AI Editing'}
          </Button>
        )}
        
        {/* AI Linked indicator when editing */}
        {isAIEditing && (
          <span className="text-xs px-2 py-0.5 rounded bg-success-light text-success border border-success/30">
            AI Linked
          </span>
        )}
        
        {/* Export buttons */}
        <Button
          onClick={() => handleExport('dxf')}
          disabled={exporting || !diagramState}
          variant="default"
          size="xs"
          title="Export to DXF (AutoCAD)"
          aria-label="Export to DXF (AutoCAD)"
        >
          {exporting ? '...' : 'DXF'}
        </Button>
        <Button
          onClick={() => handleExport('svg')}
          disabled={exporting || !diagramState}
          variant="secondary"
          size="xs"
          title="Export to SVG"
          aria-label="Export to SVG"
        >
          SVG
        </Button>

        {/* Style badge - shows the diagram standard */}
        <span className="text-xs px-2 py-0.5 rounded border bg-transparent border-info/50 text-info" title="Diagram rendering standard">
          {diagramState?.diagramStyle === 'airbus_ata' ? 'Airbus ATA' : 
           diagramState?.diagramStyle === 'default' ? 'Debug' : 
           'Boeing WDM'}
        </span>
      </div>
      
      {/* Export error */}
      {exportError && (
        <div className="flex-shrink-0 px-4 py-1">
          <p className="text-xs text-destructive">{exportError}</p>
        </div>
      )}

      {/* Canvas area */}
      <div className="flex-1 p-4 overflow-auto">
        {/* Boeing WDM Mode - Display pre-rendered SVG from C engine */}
        {boeingSvgLoading ? (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            <div className="flex flex-col items-center gap-2">
              <Spinner size="xl" className="text-link" />
              <span>Rendering Boeing WDM diagram...</span>
            </div>
          </div>
        ) : boeingSvgError ? (
          <div className="flex flex-col items-center justify-center h-64 text-destructive gap-2">
            <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm">{boeingSvgError}</span>
            <Button
              onClick={() => window.location.reload()}
              variant="secondary"
              size="xs"
              className="mt-2"
            >
              Retry
            </Button>
          </div>
        ) : boeingSvgUrl ? (
          <div className="boeing-wdm-container h-full flex flex-col">
            <div className="flex-1 min-h-[500px]">
              <SVGImageViewer
                svgUrl={boeingSvgUrl}
                title={title}
                autoFitOnLoad={true}
              />
            </div>
            <div className="flex-shrink-0 px-3 py-2 bg-info-light border border-border rounded-b-lg text-xs text-info">
              ✓ Boeing WDM rendering via C Routing Engine • Use mouse wheel to zoom, drag to pan
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            No diagram to display. Add devices and wires first.
          </div>
        )}

        {/* Stats overlay */}
        {diagramState && (
          <div className="mt-4 bg-card border border-border rounded-lg p-4">
            <div className="grid grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-muted-foreground">Devices</div>
                <div className="text-lg font-semibold text-foreground">
                  {diagramState.devices.length}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Wires</div>
                <div className="text-lg font-semibold text-foreground">
                  {diagramState.wires.length}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Crossings</div>
                <div className="text-lg font-semibold text-foreground">
                  {diagramState.crossings.length}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Total Length</div>
                <div className="text-lg font-semibold text-foreground">
                  {diagramState.wires.reduce((sum, w) => sum + (w.lengthMM || 0), 0).toFixed(1)} mm
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export default function DocumentViewerPanel({
  documents,
  activeTabId,
  editingTabId,
  isVisible,
  width,
  onSelectTab,
  onCloseTab,
  onCloseAll,
  onBack,
  onEnableEdit,
  onDisableEdit,
  onUpdate,
  onSave,
  onDocumentOperation,
  onDocumentLoaded: onDocumentLoadedProp,
  onExecutePlan: _onExecutePlan,
  onReorderTabs,
  onOpenDocument,
  onOpenDiagram,
  onOpenFlowDiagram,
  onOpenCodingProject,
  diagramState,
  activeDiagramEditId,
  isDiagramLinking,
  onEnableDiagramAIEdit,
  onDisableDiagramAIEdit,
  activeFlowchartEditId,
  onEnableFlowchartAIEdit,
  onDisableFlowchartAIEdit,
  activeCodingProjectEditId,
  onEnableCodingProjectAIEdit,
  onDisableCodingProjectAIEdit,
  mermaidVersions,
  codingProjectVersions: _codingProjectVersions,
  activeProject,
  onSwitchProject,
  onOpenProjectSwitcher,
  documentLockColors,
  userEmail,
  onOpenSettings,
  onLogout,
  onOpenPdfInViewer,
  onSendMessage,
  onOpenDocumentByNumber,
  onSetAiMode,
}: DocumentViewerPanelProps) {
  const [loadedDocuments, setLoadedDocuments] = useState<Map<number, { document: AeroDocument; version: string; s3VersionId?: string; s3JsonKey?: string }>>(new Map());
  
  // Project tab selection state (special tab, not a regular document tab)
  // Auto-select project tab when no documents are open
  const [isProjectTabSelected, setIsProjectTabSelected] = useState(documents.length === 0);
  
  // Track which tabs have been visited (rendered at least once)
  // Using ref + state to avoid re-renders when adding to the set
  const visitedTabsRef = useRef<Set<number>>(new Set());
  const [, forceUpdate] = useState(0);
  
  // Clear cached documents when project changes to prevent stale content
  const prevProjectIdRef = useRef<string | null | undefined>(activeProject?.projectId);
  useEffect(() => {
    const currentProjectId = activeProject?.projectId;
    if (prevProjectIdRef.current !== currentProjectId) {
      logger.debug('[DocumentViewerPanel] Project changed, clearing document cache');
      setLoadedDocuments(new Map());
      visitedTabsRef.current.clear();
      prevProjectIdRef.current = currentProjectId;
    }
  }, [activeProject?.projectId]);
  
  // Keep project tab selected if all documents are closed
  useEffect(() => {
    if (documents.length === 0 && !isProjectTabSelected) {
      setIsProjectTabSelected(true);
    }
  }, [documents.length, isProjectTabSelected]);
  
  // Track previous activeTabId to detect external changes (e.g., from sidebar navigation)
  const prevActiveTabIdRef = useRef<number | null>(activeTabId);
  
  // Sync isProjectTabSelected with activeTabId changes (e.g., from sidebar navigation)
  // When activeTabId CHANGES to a valid document tab, deselect the project tab
  // This prevents the race condition when clicking the project tab (activeTabId hasn't changed)
  useEffect(() => {
    const prevActiveTabId = prevActiveTabIdRef.current;
    prevActiveTabIdRef.current = activeTabId;
    
    // Only react if activeTabId actually changed
    if (activeTabId !== prevActiveTabId) {
      if (activeTabId !== null && documents.some(d => d.tabId === activeTabId)) {
        if (isProjectTabSelected) {
          setIsProjectTabSelected(false);
        }
      }
    }
  }, [activeTabId, documents, isProjectTabSelected]);
  
  // Mark active tab as visited when it changes
  useEffect(() => {
    if (activeTabId !== null && !visitedTabsRef.current.has(activeTabId)) {
      visitedTabsRef.current.add(activeTabId);
      forceUpdate(n => n + 1); // Trigger re-render to include newly visited tab
    }
  }, [activeTabId]);

  // Handle document loaded callback
  const handleDocumentLoaded = (tabId: number) => (jsonDocument: AeroDocument, version: string, s3VersionId?: string, s3JsonKey?: string) => {
    setLoadedDocuments(prev => {
      const next = new Map(prev);
      next.set(tabId, { document: jsonDocument, version, s3VersionId, s3JsonKey });
      return next;
    });
    // Notify parent that document was loaded (for Plan mode document access)
    onDocumentLoadedProp?.(tabId, jsonDocument, version, s3VersionId, s3JsonKey);
  };

  // Handle enable edit
  const handleEnableEdit = (tabId: number) => {
    const loaded = loadedDocuments.get(tabId);
    if (loaded) {
      logger.debug('[DocumentViewerPanel] Enabling edit for tab', tabId, 'with document:', loaded.document.docId);
      onEnableEdit(tabId, loaded.document, loaded.version);
    } else {
      logger.warn('[DocumentViewerPanel] Document not loaded yet for tab', tabId);
      // Wait for document to load, then enable edit
      setTimeout(() => {
        const nowLoaded = loadedDocuments.get(tabId);
        if (nowLoaded) {
          logger.debug('[DocumentViewerPanel] Document now loaded, enabling edit');
          onEnableEdit(tabId, nowLoaded.document, nowLoaded.version);
        }
      }, 500);
    }
  };

  // Get active document
  const _activeDocument = documents.find(d => d.tabId === activeTabId);

  // Always render - the pinned Projects tab should always be visible
  if (!isVisible) {
    return null;
  }

  return (
    <div
      className="flex flex-col h-full bg-background border-r border-border document-viewer-panel"
      // eslint-disable-next-line no-restricted-syntax -- Dynamic width based on resizable panel
      style={{ width: `${width}px` }}
    >
      {/* Tabs */}
      <DocumentViewerTabs
        documents={documents}
        activeTabId={isProjectTabSelected ? null : activeTabId}
        editingTabId={editingTabId}
        onSelectTab={(tabId) => {
          setIsProjectTabSelected(false);
          onSelectTab(tabId);
        }}
        onCloseTab={onCloseTab}
        onCloseAll={onCloseAll}
        onEnableEdit={handleEnableEdit}
        onReorder={onReorderTabs}
        activeProject={activeProject}
        isProjectTabSelected={isProjectTabSelected}
        onSelectProjectTab={() => {
          // If there's an active project, show project details
          // If no project is active, open the project switcher modal
          if (activeProject) {
            setIsProjectTabSelected(true);
          } else if (onOpenProjectSwitcher) {
            onOpenProjectSwitcher();
          } else {
            setIsProjectTabSelected(true);
          }
        }}
        onSwitchProject={onSwitchProject}
        documentLockColors={documentLockColors}
        userEmail={userEmail}
        onOpenSettings={onOpenSettings}
        onLogout={onLogout}
      />

      {/* Content - Render ALL tabs but hide inactive ones to preserve state */}
      <div className="flex-1 overflow-hidden relative">
        {/* Project Tab Content - Uses PagePanel for consistency with sidebar navigation */}
        {isProjectTabSelected && (
          <div className="absolute inset-0 z-20 bg-background overflow-auto">
            <PagePanel
              panelType="projects"
              onOpenDocument={(doc) => {
                setIsProjectTabSelected(false);
                if (onOpenDocument) {
                  onOpenDocument(doc);
                }
              }}
              onOpenDiagram={(diagram) => {
                setIsProjectTabSelected(false);
                if (onOpenDiagram) {
                  onOpenDiagram(diagram);
                }
              }}
              onOpenFlowDiagram={(diagram) => {
                setIsProjectTabSelected(false);
                if (onOpenFlowDiagram) {
                  onOpenFlowDiagram(diagram);
                }
              }}
              onOpenCodingProject={(codingProject) => {
                setIsProjectTabSelected(false);
                if (onOpenCodingProject) {
                  onOpenCodingProject(codingProject);
                }
              }}
              onOpenWorkspace={onSwitchProject ? (projectId) => onSwitchProject(projectId) : undefined}
              activeProjectId={activeProject?.projectId}
              autoSelectActiveProject={true}  // When closing all tabs, go to active project details
            />
          </div>
        )}
        
        {!isProjectTabSelected && documents.length === 0 ? (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <div className="text-center">
              <div className="text-4xl mb-4">📄</div>
              <div>Select a document tab to view</div>
            </div>
          </div>
        ) : !isProjectTabSelected && (
          // Only render tabs that have been visited (active at least once)
          // This prevents loading PDFs/content for tabs that haven't been viewed
          documents
            .filter((doc) => visitedTabsRef.current.has(doc.tabId))
            .map((doc) => {
            const isActive = doc.tabId === activeTabId;
            
            return (
              <div
                key={doc.tabId}
                className={`absolute inset-0 flex flex-col ${isActive ? 'z-10 visible flex' : 'z-0 invisible hidden'}`}
              >
                {doc.tabType === 'page' && doc.panelType ? (
                  // Render page panel
                  <PagePanel
                    panelType={doc.panelType}
                    searchQuery={doc.searchQuery}
                    onOpenDocument={onOpenDocument || (() => {})}
                    onOpenDiagram={onOpenDiagram}
                    onOpenFlowDiagram={onOpenFlowDiagram}
                    onOpenCodingProject={onOpenCodingProject}
                    onBack={() => onBack(doc.tabId)}
                    onOpenWorkspace={onSwitchProject ? (projectId) => onSwitchProject(projectId) : undefined}
                    activeProjectId={activeProject?.projectId}
                  />
                ) : doc.tabType === 'diagram' && doc.diagramId ? (
                  // Render diagram canvas with ATA component support
                  <DiagramCanvasWrapper
                    title={doc.title || 'Wiring Diagram'}
                    diagramId={doc.diagramId}
                    projectId={doc.projectId || ''}
                    diagramState={diagramState}
                    width={Math.max(400, width - 48)}
                    isAIEditing={activeDiagramEditId === doc.diagramId}
                    isLinking={isDiagramLinking}
                    onEnableAIEdit={() => onEnableDiagramAIEdit?.(doc.diagramId!, doc.title || 'Wiring Diagram', doc.projectId || '')}
                    onDisableAIEdit={onDisableDiagramAIEdit}
                  />
                ) : doc.tabType === 'mermaid' && doc.mermaidDiagramId ? (
                  // Render flow diagram (Mermaid-generated)
                  <FlowDiagramViewer
                    title={doc.title || 'Flow Diagram'}
                    mermaidDiagramId={doc.mermaidDiagramId}
                    projectId={doc.projectId || ''}
                    svgUrl={doc.svgUrl}
                    mermaidType={doc.mermaidType}
                    isAIEditing={activeFlowchartEditId === doc.mermaidDiagramId}
                    isLinking={isDiagramLinking}
                    onEnableAIEdit={() => onEnableFlowchartAIEdit?.(doc.mermaidDiagramId!, doc.title || 'Flow Diagram', doc.projectId || '')}
                    onDisableAIEdit={onDisableFlowchartAIEdit}
                    version={mermaidVersions?.[doc.mermaidDiagramId]}
                  />
                ) : doc.tabType === 'coding' && doc.codingProjectId ? (
                  // Render requirements index (micro-coding Level 1)
                  <RequirementsIndex
                    codingProjectId={doc.codingProjectId}
                    projectId={doc.projectId || ''}
                    isAIEditing={activeCodingProjectEditId === doc.codingProjectId}
                    isLinking={isDiagramLinking}
                    onEnableAIEdit={() => onEnableCodingProjectAIEdit?.(doc.codingProjectId!, doc.title || 'Coding Project', doc.projectId || '')}
                    onDisableAIEdit={onDisableCodingProjectAIEdit}
                    onRequirementClick={(reqId) => onOpenDocument?.({
                      id: `micro-${reqId}`,
                      documentNumber: `MICRO-${reqId}`,
                      projectId: doc.codingProjectId || doc.projectId || '',
                      title: reqId,
                      type: 'project',
                      canEdit: false,
                    })}
                  />
                ) : doc.pdfUrl ? (
                  // Direct PDF viewer for generated PDFs
                  <PdfViewer pdfUrl={doc.pdfUrl} documentTitle={doc.title} />
                ) : doc.documentNumber && doc.type ? (
                  // Render document content (including plan documents)
                  <DocumentViewerContent
                    key={`${doc.projectId}-${doc.documentNumber}-${doc.tabId}`}
                    document={{
                      id: doc.id,
                      tabId: doc.tabId,
                      documentNumber: doc.documentNumber,
                      projectId: doc.projectId,
                      title: doc.title,
                      type: doc.type as 'project' | 'company' | 'standard' | 'prior-project' | 'plan',
                      canEdit: doc.canEdit ?? false,
                    }}
                    isEditing={doc.tabId === editingTabId}
                    onEnableEdit={() => handleEnableEdit(doc.tabId)}
                    onDisableEdit={onDisableEdit}
                    onBack={() => onBack(doc.tabId)}
                    onDocumentLoaded={handleDocumentLoaded(doc.tabId)}
                    onUpdate={onUpdate}
                    onSave={onSave}
                    onDocumentOperation={onDocumentOperation}
                    onOpenPdfInViewer={onOpenPdfInViewer}
                    externalSyncDocument={doc.jsonDocument}
                    onSendMessage={onSendMessage}
                    onOpenDocument={onOpenDocumentByNumber}
                    onSetAiMode={onSetAiMode}
                  />
                ) : (
                  // Unknown tab type
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    <div className="text-center">
                      <div className="text-4xl mb-4">❓</div>
                      <div>Unknown tab type</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
